import { hash } from 'rsvp';
import type { DS } from 'ember-data';
import type Transition from '@ember/routing/transition';
import { type Registry as Services, service } from '@ember/service';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import ProtectedRoute from 'uplisting-frontend/pods/base/protected-route';
import { markInboxMessageRead } from 'uplisting-frontend/pods/-sidebar/utils';
import SidebarEnquiryMessagesController from 'uplisting-frontend/pods/-sidebar/enquiries/messages/controller';

interface IParams {
  replyId?: string;
}

interface IModel {
  enquiry: ModelRegistry['enquiry'];
  reply?: Promise<ModelRegistry['contextual-reply']>;
  property: Promise<DS.PromiseObject<ModelRegistry['property']>>;
  messagingChannels: Promise<
    DS.PromiseArray<ModelRegistry['messaging-channel']>
  >;
}

export default abstract class SidebarEnquiriesMessagesRoute extends ProtectedRoute {
  @service('repositories/property')
  propertyRepository!: Services['repositories/property'];

  @service('repositories/contextual-reply')
  contextualReplyRepository!: Services['repositories/contextual-reply'];

  @service('repositories/messaging-channel')
  messagingChannelRepository!: Services['repositories/messaging-channel'];

  @service sentry!: Services['sentry'];

  abstract parentRoute: string;

  model(params: IParams) {
    const enquiry = this.modelFor(this.parentRoute) as ModelRegistry['enquiry'];

    const data: IModel = {
      enquiry,
      property: this.propertyRepository.findRecord(enquiry.property.id, {
        reload: true,
      }),
      messagingChannels: this.messagingChannelRepository.query({
        enquiry_id: enquiry.id,
      }),
    };

    if (params.replyId) {
      data.reply = this.contextualReplyRepository.find(
        params.replyId,
        data.enquiry,
      );
    }

    return hash(data);
  }

  setupController(
    controller: SidebarEnquiryMessagesController,
    model,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);

    Object.assign(controller, model);
  }

  afterModel(model: IModel, transition: Transition): void {
    super.afterModel(model, transition);

    void markInboxMessageRead(this, model.enquiry.inboxMessage);
  }

  resetController(
    controller: SidebarEnquiryMessagesController,
    isExiting: boolean,
    transition: Transition,
  ): void {
    super.resetController(controller, isExiting, transition);

    controller.reply = undefined;
    controller.replyId = undefined;

    this.contextualReplyRepository.unloadAll();
  }
}
